import { useState, useCallback } from 'react';
import { TrainingApiService } from '../services/trainingApiService';

interface UseModelDownloadReturn {
    showModelModal: boolean;
    isDownloading: boolean;
    downloadError: string | null;

    handleSaveModel: (uploadSessionId: string | null) => Promise<boolean>;
    closeModelModal: () => void;
}

export const useModelDownload = (
    onError: (message: string) => void
): UseModelDownloadReturn => {
    const [showModelModal, setShowModelModal] = useState(false);
    const [isDownloading, setIsDownloading] = useState(false);
    const [downloadError, setDownloadError] = useState<string | null>(null);

    const handleSaveModel = useCallback(async (uploadSessionId: string | null): Promise<boolean> => {
        if (!uploadSessionId) {
            onError('No active session for model download');
            return false;
        }

        setIsDownloading(true);
        setDownloadError(null);

        try {
            const result = await TrainingApiService.saveModel(uploadSessionId);

            if (result.success) {
                // Open modal with download options
                setShowModelModal(true);
                return true;
            } else {
                const errorMsg = result.message || result.error || 'Unknown error';
                setDownloadError(errorMsg);
                onError(`Model save error: ${errorMsg}`);
                return false;
            }
        } catch (error) {
            const errorMessage = error instanceof Error ? error.message : 'Unknown error';
            setDownloadError(errorMessage);
            onError(`Model save error: ${errorMessage}`);
            return false;
        } finally {
            setIsDownloading(false);
        }
    }, [onError]);

    const closeModelModal = useCallback(() => {
        setShowModelModal(false);
        setDownloadError(null);
    }, []);

    return {
        showModelModal,
        isDownloading,
        downloadError,
        handleSaveModel,
        closeModelModal
    };
};
